import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart3, TrendingUp, ScatterChart, PieChart, Settings, Code, ChevronDown, ChevronUp } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { multiSymbolApi } from '@/services/api/multi-symbol'
import { getDefaultDslForType } from './default-dsl-templates'
import type { AnalysisType, MultiSymbolDataset } from '@/types/multi-symbol'
import { showErrorToast } from '@/utils/error-handler'

const PRESET_TYPES = ['comparison', 'correlation', 'spread', 'basket']
function isPresetType(analysisType: string): boolean {
  return PRESET_TYPES.includes(analysisType)
}

interface AnalysisConfigProps {
  selectedDatasets: MultiSymbolDataset[]
  dslCode: string
  analysisType: string
  analysisOptions: Record<string, any>
  onDslCodeChange: (code: string) => void
  onAnalysisTypeChange: (type: string) => void
  onAnalysisOptionsChange: (options: Record<string, any>) => void
}

const ANALYSIS_TYPE_ICONS = {
  comparison: BarChart3,
  correlation: ScatterChart,
  spread: TrendingUp,
  basket: PieChart,
  custom: Settings,
}

const ANALYSIS_TYPE_COLORS = {
  comparison: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  correlation: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  spread: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  basket: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  custom: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
}

export default function AnalysisConfig({
  selectedDatasets,
  dslCode,
  analysisType,
  analysisOptions,
  onDslCodeChange,
  onAnalysisTypeChange,
  onAnalysisOptionsChange,
}: AnalysisConfigProps) {
  const { t } = useTranslation()
  const [analysisTypes, setAnalysisTypes] = useState<AnalysisType[]>([])
  // For preset types: hide DSL editor by default; user can "Edit DSL" to show it
  const [showDslEditor, setShowDslEditor] = useState(false)

  const isPreset = isPresetType(analysisType)
  const defaultDsl = getDefaultDslForType(analysisType)
  const isUsingDefaultScript = isPreset && dslCode.trim() === defaultDsl.trim()

  // When switching to a preset and editor was open, keep it open if they had customized
  useEffect(() => {
    if (analysisType === 'custom') setShowDslEditor(true)
    else if (isUsingDefaultScript) setShowDslEditor(false)
  }, [analysisType, isUsingDefaultScript])

  // Load analysis types
  useEffect(() => {
    const loadAnalysisTypes = async () => {
      try {
        const response = await multiSymbolApi.getAnalysisTypes()
        setAnalysisTypes(response.analysisTypes)
      } catch (error) {
        showErrorToast(error, t('multiSymbol.analysisTypesLoadError'))
        // Fallback to default types
        setAnalysisTypes([
          {
            type: 'comparison',
            name: 'Comparison',
            description: 'Basic comparison across symbols',
            minSymbols: 2,
            maxSymbols: 20
          },
          {
            type: 'correlation',
            name: 'Correlation',
            description: 'Correlation analysis between symbols',
            minSymbols: 2,
            maxSymbols: 20
          },
          {
            type: 'spread',
            name: 'Spread',
            description: 'Spread analysis (pairs trading)',
            minSymbols: 2,
            maxSymbols: 10
          },
          {
            type: 'basket',
            name: 'Basket',
            description: 'Basket/ETF-like analysis',
            minSymbols: 3,
            maxSymbols: 20
          },
          {
            type: 'custom',
            name: 'Custom',
            description: 'Custom analysis using DSL',
            minSymbols: 1,
            maxSymbols: 20
          }
        ])
      }
    }

    loadAnalysisTypes()
  }, [])

  // Get selected analysis type details
  const selectedTypeDetails = analysisTypes.find(type => type.type === analysisType)

  // Check if current selection meets requirements
  const totalSymbols = selectedDatasets.reduce((sum, ds) => {
    if ('symbols' in ds && ds.symbols) {
      return sum + ds.symbols.length
    } else {
      return sum + 1
    }
  }, 0)

  const isValidSelection = selectedTypeDetails
    ? totalSymbols >= selectedTypeDetails.minSymbols && totalSymbols <= selectedTypeDetails.maxSymbols
    : false

  return (
    <div className="space-y-6">
      {/* Analysis Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle>{t('multiSymbol.analysisType')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {analysisTypes.map((type) => {
              const Icon = ANALYSIS_TYPE_ICONS[type.type as keyof typeof ANALYSIS_TYPE_ICONS] || Settings
              const isSelected = analysisType === type.type
              const colorClass = ANALYSIS_TYPE_COLORS[type.type as keyof typeof ANALYSIS_TYPE_COLORS] || 'bg-gray-100 text-gray-800'

              return (
                <Card
                  key={type.type}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'ring-2 ring-primary shadow-md' : ''
                  }`}
                  onClick={() => onAnalysisTypeChange(type.type)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${colorClass}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-medium">{type.name}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {type.description}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {t('multiSymbol.symbolsRange', { min: type.minSymbols, max: type.maxSymbols })}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Validation Message */}
          {selectedDatasets.length > 0 && selectedTypeDetails && (
            <div className={`p-3 rounded-lg ${
              isValidSelection
                ? 'bg-green-50 text-green-800 dark:bg-green-900 dark:text-green-300'
                : 'bg-red-50 text-red-800 dark:bg-red-900 dark:text-red-300'
            }`}>
              <div className="flex items-center gap-2">
                <span className="font-medium">
                  {isValidSelection ? '✓' : '⚠'}
                </span>
                <span>
                  {isValidSelection
                    ? t('multiSymbol.symbolsSelectedFor', { count: totalSymbols, name: selectedTypeDetails.name }) + ' - ' + t('multiSymbol.readyToAnalyze')
                    : t('multiSymbol.symbolsSelectedFor', { count: totalSymbols, name: selectedTypeDetails.name }) + ' - ' + t('multiSymbol.requiresSymbols', { min: selectedTypeDetails.minSymbols, max: selectedTypeDetails.maxSymbols })
                  }
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* DSL Script: hidden for presets (using default) unless user clicks "Edit DSL"; always visible for Custom */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code className="w-4 h-4" />
            {t('multiSymbol.dslScript')}
            {isPreset && !showDslEditor && (
              <Badge variant="secondary" className="text-xs font-normal">
                {t('multiSymbol.usingDefaultScript')}
              </Badge>
            )}
            {analysisType === 'custom' && (
              <Badge variant="outline" className="text-xs font-normal">
                {t('multiSymbol.required')}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isPreset && !showDslEditor ? (
            <>
              <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('multiSymbol.defaultScriptDescription', { name: selectedTypeDetails?.name ?? analysisType }) }} />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDslEditor(true)}
                className="gap-2"
              >
                <Code className="w-4 h-4" />
                {t('multiSymbol.editDslScript')}
                <ChevronDown className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              {isPreset && showDslEditor && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowDslEditor(false)
                    onDslCodeChange(defaultDsl)
                  }}
                  className="mb-2 gap-2 text-muted-foreground"
                >
                  <ChevronUp className="w-4 h-4" />
                  {t('multiSymbol.useDefaultAndHide')}
                </Button>
              )}
              <Label htmlFor="dsl-code">
                {analysisType === 'custom'
                  ? t('multiSymbol.dslLabelCustom')
                  : t('multiSymbol.dslLabelPreset')}
              </Label>
              <Textarea
                id="dsl-code"
                placeholder={`# DSL for ${analysisType} analysis\nstats by symbol:\n  count(*) as total_bars\n  avg(close) as avg_price\noutput stats`}
                value={dslCode}
                onChange={(e) => onDslCodeChange(e.target.value)}
                rows={14}
                className="font-mono text-sm"
              />
              <p className="text-sm text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('multiSymbol.symbolColumnHint') }} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Analysis Options */}
      {analysisType && (
        <Card>
          <CardHeader>
            <CardTitle>{t('multiSymbol.analysisOptions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analysisType === 'correlation' && (
                <div>
                  <Label htmlFor="correlation-threshold">{t('multiSymbol.correlationThreshold')}</Label>
                  <Select
                    value={String(analysisOptions.correlationThreshold || '0.3')}
                    onValueChange={(value: string) =>
                      onAnalysisOptionsChange({
                        ...analysisOptions,
                        correlationThreshold: parseFloat(value)
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0.1">{t('multiSymbol.weak')}</SelectItem>
                      <SelectItem value="0.3">{t('multiSymbol.moderate')}</SelectItem>
                      <SelectItem value="0.5">{t('multiSymbol.strong')}</SelectItem>
                      <SelectItem value="0.7">{t('multiSymbol.veryStrong')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {analysisType === 'comparison' && (
                <div>
                  <Label htmlFor="sort-by">{t('multiSymbol.sortResultsBy')}</Label>
                  <Select
                    value={analysisOptions.sortBy || 'avg_return'}
                    onValueChange={(value) =>
                      onAnalysisOptionsChange({
                        ...analysisOptions,
                        sortBy: value
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="avg_return">{t('multiSymbol.avgReturn')}</SelectItem>
                      <SelectItem value="volatility">{t('multiSymbol.volatility')}</SelectItem>
                      <SelectItem value="sharpe_ratio">{t('multiSymbol.sharpeRatio')}</SelectItem>
                      <SelectItem value="max_drawdown">{t('multiSymbol.maxDrawdown')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {analysisType === 'basket' && (
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="weighting-method">{t('multiSymbol.weightingMethod')}</Label>
                    <Select
                      value={analysisOptions.weightingMethod || 'equal'}
                      onValueChange={(value) =>
                        onAnalysisOptionsChange({
                          ...analysisOptions,
                          weightingMethod: value
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equal">{t('multiSymbol.equalWeight')}</SelectItem>
                        <SelectItem value="market_cap">{t('multiSymbol.marketCapWeighted')}</SelectItem>
                        <SelectItem value="momentum">{t('multiSymbol.momentumWeighted')}</SelectItem>
                        <SelectItem value="risk_parity">{t('multiSymbol.riskParity')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="rebalance-frequency">{t('multiSymbol.rebalanceFrequency')}</Label>
                    <Select
                      value={analysisOptions.rebalanceFrequency || 'monthly'}
                      onValueChange={(value) =>
                        onAnalysisOptionsChange({
                          ...analysisOptions,
                          rebalanceFrequency: value
                        })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">{t('multiSymbol.daily')}</SelectItem>
                        <SelectItem value="weekly">{t('multiSymbol.weekly')}</SelectItem>
                        <SelectItem value="monthly">{t('multiSymbol.monthly')}</SelectItem>
                        <SelectItem value="quarterly">{t('multiSymbol.quarterly')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}